<template>
	<div>
		<v-dialog v-model="dialogOpen" max-width="720" persistent scrollable>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>{{ dialogTitle }}</span>
					<v-spacer />
					<v-btn icon :disabled="saving" @click="fecharDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider />

				<v-card-text class="pt-6 pb-0">
					<v-form ref="cadastroForm" v-model="formValid" lazy-validation>
						<v-row dense>
							<v-col cols="12" md="8">
								<v-text-field
									v-model="form.descricao"
									label="Descricao"
									outlined
									dense
									:rules="[rules.required]"
								/>
							</v-col>

							<v-col cols="12" md="4">
								<v-select
									v-model="form.tipo"
									:items="tiposLancamento"
									label="Tipo"
									outlined
									dense
									:rules="[rules.required]"
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-combobox
									v-model="form.categoria"
									:items="categorias"
									label="Categoria"
									outlined
									dense
									hide-selected
									:rules="[rules.required]"
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-select
									v-model="form.conta_bancaria_id"
									:items="contasBancarias"
									label="Conta bancaria"
									no-data-text="Conta nao cadastrada"
									item-text="nome"
									item-value="id"
									outlined
									dense
									clearable
									hint="Opcional. Use para vincular o lançamento a uma conta cadastrada."
									persistent-hint
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-autocomplete
									v-model="form.veiculo_ids"
									:items="veiculosOptions"
									item-text="label"
									item-value="value"
									label="Veiculos"
									no-data-text="Veiculo nao cadastrado"
									outlined
									dense
									multiple
									small-chips
									deletable-chips
									clearable
									hint="Opcional. Use para vincular o lançamento a um ou mais veículos da transportadora."
									persistent-hint
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									v-model="form.valor"
									label="Valor"
									outlined
									dense
									prefix="R$"
									:rules="[rules.required, rules.currency]"
									@input="onCurrencyInput"
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									v-model="form.data_prevista"
									label="Data prevista"
									type="date"
									outlined
									dense
									:rules="[rules.required]"
								/>
							</v-col>

							<v-col v-if="showFutureInstallmentsEditor" cols="12">
								<div class="financeiro-installments-editor">
									<div class="financeiro-installments-editor__title">Datas das parcelas futuras</div>
									<div class="financeiro-installments-editor__subtitle">
										Edite as datas previstas das parcelas pendentes a partir da proxima parcela.
									</div>

									<v-row dense class="mt-1">
										<v-col
											v-for="item in futureInstallments"
											:key="item.id"
											cols="12"
											md="6"
										>
											<v-text-field
												v-model="futureInstallmentDates[item.id]"
												:label="`Parcela ${item.installmentNumber}/${item.totalInstallments}`"
												type="date"
												outlined
												dense
											/>
										</v-col>
									</v-row>
								</div>
							</v-col>

							<v-col v-if="!isEditMode" cols="12" md="6">
								<v-switch
									v-model="form.parcelado"
									class="financeiro-switch mt-0"
									label="Cadastrar parcelas para os proximos meses"
									inset
									color="#12b57a"
								/>
							</v-col>

							<v-col v-if="!isEditMode && form.parcelado" cols="12" md="6">
								<v-text-field
									v-model.number="form.quantidade_parcelas"
									label="Quantidade de parcelas"
									type="number"
									min="2"
									outlined
									dense
									:rules="form.parcelado ? [rules.required, rules.installments] : []"
								/>
							</v-col>

							<v-col v-if="!isEditMode && form.parcelado" cols="12">
								<div class="financeiro-installments-hint">
									Serao criados {{ installmentsCount }} lancamentos mensais a partir de
									<strong>{{ formatSummaryDate(newInstallmentsStartDate) }}</strong>
									ate
									<strong>{{ formatSummaryDate(newInstallmentsEndDate) }}</strong>.
									As parcelas ficam pendentes para baixa individual.
								</div>
							</v-col>

							<v-col v-if="showNewInstallmentsEditor" cols="12">
								<div class="financeiro-installments-editor">
									<div class="financeiro-installments-editor__title">Datas das parcelas</div>
									<div class="financeiro-installments-editor__subtitle">
										Revise e ajuste as datas previstas de cada parcela antes de salvar.
									</div>

									<v-row dense class="mt-1">
										<v-col
											v-for="item in newInstallments"
											:key="item.index"
											cols="12"
											md="6"
										>
											<v-text-field
												v-model="newInstallmentDates[item.index]"
												:label="`Parcela ${item.installmentNumber}/${item.totalInstallments}`"
												type="date"
												outlined
												dense
											/>
										</v-col>
									</v-row>
								</div>
							</v-col>

							<v-col cols="12" md="6">
								<v-switch
									v-model="form.estaBaixado"
									class="financeiro-switch mt-0"
									label="Ja foi pago ou recebido"
									inset
									color="#12b57a"
									:disabled="!isEditMode && form.parcelado"
								/>
							</v-col>

							<v-col v-if="form.estaBaixado" cols="12" md="6">
								<v-text-field
									v-model="form.data_baixa"
									:label="form.tipo === 'Receber' ? 'Data do recebimento' : 'Data do pagamento'"
									type="date"
									outlined
									dense
									:rules="form.estaBaixado ? [rules.required] : []"
								/>
							</v-col>

							<v-col cols="12">
								<v-textarea
									v-model="form.observacoes"
									label="Observacoes"
									outlined
									dense
									rows="3"
									auto-grow
								/>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>

				<v-card-actions class="px-6 pb-6 pt-3">
					<v-spacer />
					<v-btn text :disabled="saving" @click="fecharDialog">Cancelar</v-btn>
					<v-btn class="financeiro-save-btn" depressed :loading="saving" :disabled="saving" @click="salvarLancamento">
						{{ saveButtonLabel }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import financeiroServices from './financeiroServices';
import {
	formatCurrencyFieldValue,
	formatCurrencyInputValue,
	parseCurrency,
} from './financeiroCurrency';

const MIN_INSTALLMENTS = 2;

function padNumber(value) {
	return String(value).padStart(2, '0');
}

function normalizeInstallmentsCount(value) {
	const parsed = Number.parseInt(value, 10);
	return Number.isInteger(parsed) && parsed >= MIN_INSTALLMENTS ? parsed : 1;
}

function normalizeText(value) {
	const normalized = String(value || '').trim();
	return normalized || '';
}

function addMonthsToDate(dateValue, monthsToAdd) {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateValue || ''))) {
		return dateValue;
	}

	const [year, month, day] = String(dateValue).split('-').map((part) => Number(part));
	const targetBaseDate = new Date(Date.UTC(year, month - 1 + monthsToAdd, 1));
	const targetYear = targetBaseDate.getUTCFullYear();
	const targetMonth = targetBaseDate.getUTCMonth();
	const lastDayOfMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
	const targetDay = Math.min(day, lastDayOfMonth);

	return `${targetYear}-${padNumber(targetMonth + 1)}-${padNumber(targetDay)}`;
}

function parseInstallmentDescription(descricao) {
	const normalized = String(descricao || '').trim();
	const match = normalized.match(/^(.*)\((\d+)\/(\d+)\)\s*$/);

	if (!match) {
		return null;
	}

	const installmentNumber = Number(match[2]);
	const totalInstallments = Number(match[3]);

	if (!Number.isInteger(installmentNumber) || !Number.isInteger(totalInstallments) || totalInstallments < 2) {
		return null;
	}

	return {
		baseDescription: String(match[1] || '').trim(),
		installmentNumber,
		totalInstallments,
	};
}

const createInitialForm = () => ({
	descricao: '',
	tipo: 'Receber',
	categoria: '',
	conta_bancaria_id: null,
	veiculo_ids: [],
	valor: '',
	data_prevista: new Date().toISOString().slice(0, 10),
	data_baixa: '',
	estaBaixado: false,
	parcelado: false,
	quantidade_parcelas: MIN_INSTALLMENTS,
	observacoes: '',
});

export default {
	name: 'CadastroFinanceiroDialog',

	props: {
		value: {
			type: Boolean,
			default: false,
		},
		lancamento: {
			type: Object,
			default: null,
		},
		lancamentos: {
			type: Array,
			default: () => [],
		},
		categorias: {
			type: Array,
			default: () => [],
		},
		contasBancarias: {
			type: Array,
			default: () => [],
		},
		veiculos: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			dialogOpen: false,
			formValid: false,
			saving: false,
			tiposLancamento: ['Receber', 'Pagar'],
			form: createInitialForm(),
			futureInstallmentDates: {},
			newInstallmentDates: {},
			rules: {
				required: (v) => Boolean(v || v === 0) || 'Campo obrigatorio',
				installments: (v) => Number.isInteger(Number(v)) && Number(v) >= MIN_INSTALLMENTS || 'Informe ao menos 2 parcelas',
				currency: (v) => {
					if (!v) {
						return true;
					}

						const numericValue = parseCurrency(v);
					return Number.isFinite(numericValue) && numericValue >= 0 || 'Informe um valor valido';
				},
			},
		};
	},

	computed: {
		isEditMode() {
			return Boolean(this.lancamento && this.lancamento.id);
		},

		dialogTitle() {
			return this.isEditMode ? 'Editar Lancamento' : 'Novo Lancamento';
		},

		saveButtonLabel() {
			if (this.isEditMode) {
				return 'Salvar alteracoes';
			}

			return this.form.parcelado ? 'Salvar parcelas' : 'Salvar lancamento';
		},

		currentInstallmentInfo() {
			return parseInstallmentDescription(this.lancamento?.descricao);
		},

		veiculosOptions() {
			return this.veiculos.map((item) => ({
				label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
				value: item.id,
			}));
		},

		installmentsCount() {
			return normalizeInstallmentsCount(this.form.quantidade_parcelas);
		},

		lastInstallmentDate() {
			return addMonthsToDate(this.form.data_prevista, this.installmentsCount - 1);
		},

		newInstallmentsStartDate() {
			const firstInstallment = this.newInstallments[0];
			if (!firstInstallment) {
				return this.form.data_prevista;
			}

			return this.normalizeDateInput(
				this.newInstallmentDates[firstInstallment.index],
				firstInstallment.defaultDate
			);
		},

		newInstallmentsEndDate() {
			const lastInstallment = this.newInstallments[this.newInstallments.length - 1];
			if (!lastInstallment) {
				return this.lastInstallmentDate;
			}

			return this.normalizeDateInput(
				this.newInstallmentDates[lastInstallment.index],
				lastInstallment.defaultDate
			);
		},

		futureInstallments() {
			if (!this.isEditMode || !this.currentInstallmentInfo) {
				return [];
			}

			const { baseDescription, installmentNumber, totalInstallments } = this.currentInstallmentInfo;
			const current = this.lancamento || {};

			return this.lancamentos
				.map((item) => {
					const info = parseInstallmentDescription(item?.descricao);

					if (!info) {
						return null;
					}

					const sameGroup = info.baseDescription === baseDescription
						&& info.totalInstallments === totalInstallments
						&& String(item?.tipo || '') === String(current.tipo || '')
						&& String(item?.categoria || '') === String(current.categoria || '')
						&& Number(item?.valor || 0) === Number(current.valor || 0)
						&& String(item?.created_at || '') === String(current.created_at || '');

					if (!sameGroup || info.installmentNumber <= installmentNumber || String(item?.status || '') === 'Baixado') {
						return null;
					}

					return {
						...item,
						installmentNumber: info.installmentNumber,
						totalInstallments: info.totalInstallments,
					};
				})
				.filter(Boolean)
				.sort((a, b) => a.installmentNumber - b.installmentNumber);
		},

		showFutureInstallmentsEditor() {
			return this.futureInstallments.length > 0;
		},

		newInstallments() {
			if (this.isEditMode || !this.form.parcelado) {
				return [];
			}

			return Array.from({ length: this.installmentsCount }, (_, index) => ({
				index,
				installmentNumber: index + 1,
				totalInstallments: this.installmentsCount,
				defaultDate: addMonthsToDate(this.form.data_prevista, index),
			}));
		},

		showNewInstallmentsEditor() {
			return this.newInstallments.length > 0;
		},
	},

	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.dialogOpen = newValue;
			},
		},

		dialogOpen(newValue) {
			this.$emit('input', newValue);

			if (newValue) {
				this.preencherFormulario();
				return;
			}

			if (!this.saving) {
				this.resetForm();
			}
		},

		lancamento: {
			deep: true,
			handler() {
				if (this.dialogOpen) {
					this.preencherFormulario();
				}
			},
		},

		lancamentos: {
			deep: true,
			handler() {
				if (this.dialogOpen && this.isEditMode) {
					this.syncFutureInstallmentDates();
				}
			},
		},

		'form.estaBaixado'(newValue) {
			if (newValue && this.form.parcelado) {
				this.form.parcelado = false;
			}

			if (newValue && !this.form.data_baixa) {
				this.form.data_baixa = new Date().toISOString().slice(0, 10);
			}

			if (!newValue) {
				this.form.data_baixa = '';
			}
		},

		'form.parcelado'(newValue) {
			if (!newValue) {
				this.form.quantidade_parcelas = MIN_INSTALLMENTS;
				this.newInstallmentDates = {};
				return;
			}

			if (this.form.estaBaixado) {
				this.form.estaBaixado = false;
			}

			if (normalizeInstallmentsCount(this.form.quantidade_parcelas) < MIN_INSTALLMENTS) {
				this.form.quantidade_parcelas = MIN_INSTALLMENTS;
			}

			this.syncNewInstallmentDates();
		},

		'form.quantidade_parcelas'() {
			if (this.form.parcelado && !this.isEditMode) {
				this.syncNewInstallmentDates();
			}
		},

		'form.data_prevista'() {
			if (this.form.parcelado && !this.isEditMode) {
				this.syncNewInstallmentDates();
			}
		},
	},

	methods: {
		normalizeDateInput(value, fallback = '') {
			if (!value) {
				return fallback;
			}

			const normalized = String(value).trim();

			if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
				return normalized;
			}

			if (/^\d{4}-\d{2}-\d{2}T/.test(normalized) || /^\d{4}-\d{2}-\d{2}\s/.test(normalized)) {
				return normalized.slice(0, 10);
			}

			if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) {
				const [day, month, year] = normalized.split('/');
				return `${year}-${month}-${day}`;
			}

			return fallback;
		},

		onCurrencyInput(value) {
			this.form.valor = formatCurrencyInputValue(value);
		},

		formatSummaryDate(value) {
			if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) {
				return '--/--/----';
			}

			const [year, month, day] = String(value).split('-');
			return `${day}/${month}/${year}`;
		},

		preencherFormulario() {
			const lancamento = this.lancamento;

			if (!lancamento) {
				this.form = createInitialForm();
				this.futureInstallmentDates = {};
				this.newInstallmentDates = {};
				return;
			}

			this.form = {
				descricao: lancamento.descricao || '',
				tipo: lancamento.tipo || 'Receber',
				categoria: lancamento.categoria || '',
				conta_bancaria_id: lancamento.conta_bancaria_id || null,
				veiculo_ids: Array.isArray(lancamento.veiculo_ids)
					? [...lancamento.veiculo_ids]
					: (lancamento.veiculo_id ? [lancamento.veiculo_id] : []),
				valor: formatCurrencyFieldValue(lancamento.valor),
				data_prevista: this.normalizeDateInput(lancamento.data_prevista, new Date().toISOString().slice(0, 10)),
				data_baixa: this.normalizeDateInput(lancamento.data_baixa, ''),
				estaBaixado: Boolean(lancamento.data_baixa),
				parcelado: false,
				quantidade_parcelas: MIN_INSTALLMENTS,
				observacoes: lancamento.observacoes || '',
			};

			this.syncFutureInstallmentDates();
		},

		syncFutureInstallmentDates() {
			const nextDates = {};

			for (const item of this.futureInstallments) {
				nextDates[item.id] = this.normalizeDateInput(
					this.futureInstallmentDates[item.id] || item.data_prevista,
					this.normalizeDateInput(item.data_prevista, '')
				);
			}

			this.futureInstallmentDates = nextDates;
		},

		syncNewInstallmentDates() {
			const nextDates = {};

			for (const item of this.newInstallments) {
				nextDates[item.index] = this.normalizeDateInput(
					this.newInstallmentDates[item.index],
					item.defaultDate
				);
			}

			this.newInstallmentDates = nextDates;
		},

		buildPayload() {
			return {
				descricao: normalizeText(this.form.descricao),
				tipo: this.form.tipo,
				categoria: normalizeText(this.form.categoria),
				conta_bancaria_id: this.form.conta_bancaria_id || null,
				veiculo_ids: Array.isArray(this.form.veiculo_ids)
					? [...this.form.veiculo_ids]
					: [],
				valor: parseCurrency(this.form.valor),
				data_prevista: this.form.data_prevista,
				data_baixa: this.form.estaBaixado ? this.form.data_baixa : null,
				observacoes: normalizeText(this.form.observacoes),
			};
		},

		buildInstallmentsPayload(basePayload) {
			return this.newInstallments.map((item) => ({
				...basePayload,
				descricao: `${basePayload.descricao} (${item.installmentNumber}/${item.totalInstallments})`,
				data_prevista: this.normalizeDateInput(
					this.newInstallmentDates[item.index],
					item.defaultDate
				),
				data_baixa: null,
			}));
		},

		resetForm() {
			this.form = createInitialForm();
			this.futureInstallmentDates = {};
			this.newInstallmentDates = {};
			this.formValid = false;
			if (this.$refs.cadastroForm) {
				this.$refs.cadastroForm.resetValidation();
			}
		},

		getFutureInstallmentUpdates() {
			return this.futureInstallments
				.map((item) => {
					const nextDate = this.normalizeDateInput(
						this.futureInstallmentDates[item.id],
						this.normalizeDateInput(item.data_prevista, '')
					);
					const currentDate = this.normalizeDateInput(item.data_prevista, '');

					if (!nextDate || nextDate === currentDate) {
						return null;
					}

					return {
						id: item.id,
						data_prevista: nextDate,
					};
				})
				.filter(Boolean);
		},

		fecharDialog() {
			if (!this.saving) {
				this.dialogOpen = false;
			}
		},

		async salvarLancamento() {
			if (!this.$refs.cadastroForm?.validate()) {
				return;
			}

			this.saving = true;

			try {
				const payload = this.buildPayload();

				let response;

				if (this.isEditMode) {
					response = await financeiroServices.atualizar(this.lancamento.id, payload);

					const futureInstallmentUpdates = this.getFutureInstallmentUpdates();
					if (futureInstallmentUpdates.length > 0) {
						await Promise.all(
							futureInstallmentUpdates.map((item) => financeiroServices.atualizar(item.id, {
								data_prevista: item.data_prevista,
							}))
						);
					}
				} else if (this.form.parcelado) {
					response = await financeiroServices.criarEmLote(this.buildInstallmentsPayload(payload));
				} else {
					response = await financeiroServices.criar(payload);
				}

				this.$emit('saved', response.data);
				this.dialogOpen = false;

				if (this.$toasted) {
					if (this.isEditMode) {
						this.$toasted.show('Lancamento atualizado com sucesso');
					} else if (this.form.parcelado) {
						this.$toasted.show(`${this.installmentsCount} parcelas cadastradas com sucesso`);
					} else {
						this.$toasted.show('Lancamento cadastrado com sucesso');
					}
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel salvar o lancamento';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.financeiro-dialog {
	border-radius: 24px;
	font-family: "Inter", sans-serif;
	border: 1px solid #e6ebf1 !important;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12) !important;
}

.financeiro-dialog__title {
	padding: 24px 24px 18px;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.financeiro-save-btn {
	background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
	padding: 0 18px !important;
	box-shadow: 0 12px 28px rgba(18, 181, 122, 0.22) !important;
}

.financeiro-switch::v-deep .v-label {
	font-size: 14px;
	color: #506076;
}

.financeiro-installments-hint {
	padding: 14px 16px;
	border-radius: 14px;
	background: #f0fdf4;
	border: 1px solid #bbf7d0;
	color: #166534;
	font-size: 13px;
	line-height: 1.5;
}

.financeiro-installments-editor {
	padding: 14px 16px;
	border-radius: 14px;
	background: #f8fbff;
	border: 1px solid #dbe7f3;
}

.financeiro-installments-editor__title {
	font-size: 14px;
	font-weight: 700;
	color: #1f2a44;
}

.financeiro-installments-editor__subtitle {
	margin-top: 4px;
	font-size: 12px;
	line-height: 1.5;
	color: #6b7a90;
}
</style>
